import React, { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import { HC } from "./theme";
import { Card, SectionHead, Avatar, Empty } from "./ui";
import { RefreshCw, Radio, Coffee, Clock, UserX, Plane, CheckCircle2, MapPin, ArrowUpRight } from "lucide-react";

const POLL_MS = 20000;

const STATUS = {
  on_duty: { label: "On duty", color: HC.green, tint: HC.greenTint, Icon: Radio },
  on_break: { label: "On break", color: HC.gold, tint: HC.goldSoft, Icon: Coffee },
  yet_to_arrive: { label: "Yet to arrive", color: HC.amber, tint: HC.amberTint, Icon: Clock },
  finished: { label: "Finished", color: HC.blue, tint: HC.blueTint, Icon: CheckCircle2 },
  on_leave: { label: "On leave", color: HC.purple, tint: HC.purpleTint, Icon: Plane },
  absent: { label: "Absent", color: HC.red, tint: HC.redTint, Icon: UserX },
};

const pretty = (hhmm) => {
  if (!hhmm) return "—";
  const h = Number(hhmm.slice(0, 2)), m = hhmm.slice(3, 5);
  return `${h % 12 || 12}:${m} ${h < 12 ? "AM" : "PM"}`;
};
const dur = (mins) => {
  if (mins === null || mins === undefined) return "—";
  const m = Math.max(0, mins);
  return m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`;
};

function Person({ p, onOpen }) {
  const s = STATUS[p.status] || STATUS.finished;
  const late = p.status === "yet_to_arrive" && (p.late_minutes || 0) > 0;
  return (
    <button onClick={() => onOpen?.(p)} data-testid={`floor-person-${p.employee_id}`}
      className="w-full flex items-center gap-3 p-2.5 rounded-xl text-left transition-colors hover:bg-[#FBF8F2]"
      style={{ background: late ? HC.amberTint : "transparent" }}>
      <span className="relative shrink-0">
        <Avatar name={p.employee_name} url={p.photo_url} size={34} />
        <span className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-white"
          style={{ background: s.color }} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1.5">
          <span className="text-[13px] font-semibold truncate" style={{ color: HC.text }}>{p.employee_name}</span>
          {p.away_from_home && (
            <span className="inline-flex items-center gap-0.5 text-[9.5px] font-bold px-1.5 py-0.5 rounded-full"
              style={{ background: HC.purpleTint, color: HC.purple }} title={`Home: ${p.home_location_name}`}>
              <ArrowUpRight size={9} /> COVERING
            </span>
          )}
        </span>
        <span className="block text-[11px] truncate" style={{ color: HC.soft }}>
          {p.position} · {p.shift_name ? `${p.shift_name} ${pretty(p.shift_start)}–${pretty(p.shift_end)}` : "No shift assigned"}
        </span>
      </span>
      <span className="text-right shrink-0">
        <span className="block text-[11.5px] font-semibold" style={{ color: s.color }}>
          {p.status === "on_duty" && `${dur(p.minutes_on_duty)} on duty`}
          {p.status === "on_break" && `${dur(p.break_minutes_so_far)} on break`}
          {p.status === "yet_to_arrive" && (late ? `${dur(p.late_minutes)} late` : "Awaited")}
          {p.status === "finished" && `Out ${pretty(p.clock_out)}`}
          {p.status === "absent" && "Absent"}
          {p.status === "on_leave" && "On leave"}
        </span>
        <span className="block text-[10.5px]" style={{ color: HC.soft }}>
          {p.clock_in ? `In ${pretty(p.clock_in)}${p.kiosk_id ? ` · ${p.kiosk_id}` : ""}` : p.shift_start ? `Due ${pretty(p.shift_start)}` : "—"}
          {p.past_shift_end ? ` · +${dur(p.overtime_minutes_live)} OT` : ""}
        </span>
      </span>
    </button>
  );
}

export default function FloorBoard({ locationId, onOpenEmployee, title = "Live Floor Board" }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback((spin = false) => {
    if (spin) setBusy(true);
    api.get("/hr/floor-board", { params: { location_id: locationId || undefined } })
      .then(setData).catch(() => {}).finally(() => setBusy(false));
  }, [locationId]);

  useEffect(() => {
    load(true);
    const iv = setInterval(() => {
      if (document.visibilityState === "visible") load(false);
    }, POLL_MS);
    return () => clearInterval(iv);
  }, [load]);

  const t = data?.totals;
  const locs = data?.locations || [];

  return (
    <Card testid="hr-floor-board">
      <SectionHead
        title={
          <span className="flex items-center gap-2">
            <span className="relative flex w-2.5 h-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-60" style={{ background: HC.green }} />
              <span className="relative inline-flex rounded-full w-2.5 h-2.5" style={{ background: HC.green }} />
            </span>
            {title}
          </span>
        }
        sub={data ? `Live from kiosk clock-ins · updated ${new Date(data.as_of).toLocaleTimeString("en-NG", { hour: "2-digit", minute: "2-digit" })} · refreshes every 20s` : "Loading the floor…"}
        right={
          <button onClick={() => load(true)} data-testid="floor-refresh"
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-xl text-[12.5px] font-semibold bg-white"
            style={{ border: `1px solid ${HC.border}`, color: HC.text }}>
            <RefreshCw size={13} className={busy ? "animate-spin" : ""} /> Refresh
          </button>
        }
      />

      {t && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5 mb-4">
          {[
            ["On floor now", t.on_floor, HC.green, HC.greenTint],
            ["On break", t.on_break, HC.gold, HC.goldSoft],
            ["Yet to arrive", t.yet_to_arrive, HC.amber, HC.amberTint],
            ["Running late", t.late, HC.red, HC.redTint],
            ["Finished", t.finished, HC.blue, HC.blueTint],
            ["Coverage", `${t.coverage_pct}%`, HC.purple, HC.purpleTint],
          ].map(([l, v, c, bg]) => (
            <div key={l} className="rounded-xl px-3 py-2.5" style={{ background: bg }} data-testid={`floor-total-${l.toLowerCase().replace(/ /g, "-")}`}>
              <div className="text-[9.5px] font-bold tracking-[0.14em]" style={{ color: c }}>{l.toUpperCase()}</div>
              <div className="font-brand font-bold text-[22px] leading-tight tabular-nums" style={{ color: HC.text }}>{v}</div>
            </div>
          ))}
        </div>
      )}

      {!data ? (
        <div className="h-40 rounded-xl animate-pulse" style={{ background: HC.borderSoft }} />
      ) : locs.length ? (
        <div className={`grid gap-3 ${locs.length > 1 ? "grid-cols-1 md:grid-cols-2 xl:grid-cols-4" : "grid-cols-1"}`}>
          {locs.map((b) => (
            <div key={b.location_id} className="rounded-xl border p-3" style={{ borderColor: HC.border, background: HC.panelSoft }}
              data-testid={`floor-loc-${b.location_id}`}>
              <div className="flex items-center gap-2 mb-1">
                <MapPin size={14} style={{ color: HC.gold }} />
                <span className="font-brand font-bold text-[14px]" style={{ color: HC.text }}>{b.location_name}</span>
                <span className="flex-1" />
                <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full"
                  style={{ background: b.coverage_pct >= 80 ? HC.greenTint : b.coverage_pct >= 50 ? HC.amberTint : HC.redTint,
                           color: b.coverage_pct >= 80 ? HC.green : b.coverage_pct >= 50 ? HC.amber : HC.red }}>
                  {b.on_floor}/{b.scheduled} on floor
                </span>
              </div>
              {!!b.by_department.length && (
                <div className="text-[10.5px] mb-2" style={{ color: HC.soft }}>
                  {b.by_department.map((d) => `${d.name} ${d.value}`).join(" · ")}
                </div>
              )}
              <div className="space-y-0.5 max-h-[420px] overflow-y-auto">
                {b.people.map((p) => <Person key={p.employee_id} p={p} onOpen={onOpenEmployee} />)}
              </div>
            </div>
          ))}
        </div>
      ) : <Empty text="Nobody is scheduled or clocked in today" />}

      <div className="mt-3 flex flex-wrap items-center gap-3">
        {Object.entries(STATUS).map(([k, s]) => (
          <span key={k} className="inline-flex items-center gap-1.5 text-[11px]" style={{ color: HC.muted }}>
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: s.color }} />{s.label}
          </span>
        ))}
      </div>
    </Card>
  );
}
